import { useParams, useNavigate } from "react-router-dom";
import { useAtomValue, useSetAtom } from "jotai";
import { Button } from "reactstrap";
import { useMemo } from "react";

import useWorkspaces from "../../../api/api-hooks/workspaces/useWorkspaces";
import { workspaceIdAtom } from "../../../atoms/WorkspaceAtoms";
import { Workspace } from "../../../types/WorkspaceInterfaces";
import { sidebarOpenAtom } from "../../../atoms/SidebarAtoms";
import AppSpinner from "../../../elements/AppSpinner";
import { IsMobileAtom } from "../../../App";
import IconLink from "../IconLink";

interface SelectButtonProps {
  onClick: () => void;
  disabled: boolean;
  icon: `mdi mdi-${string}`;
  title: string;
}

interface WorkspaceSwitcherOptionsProps {
  toggleModal?: () => void;
  closePopover: () => void;
  css?: string;
  width?: number;
}

const SelectButton = ({
  onClick,
  disabled,
  icon,
  title,
}: SelectButtonProps) => {
  return (
    <Button
      size="sm"
      color="secondary"
      outline
      block
      onClick={onClick}
      disabled={disabled}
      className="d-flex align-items-center p-3"
    >
      <span>
        <i
          className={`${icon} tw:mr-2`}
          style={{
            fontSize: 14,
            lineHeight: 1,
            position: "relative",
            top: 1,
          }}
        ></i>
      </span>
      <span className="btn-inner--text">{title}</span>
    </Button>
  );
};

const Loading = () => (
  <div className="d-flex justify-content-center">
    <AppSpinner />
  </div>
);

const WorkspaceSwitcherOptions = ({
  toggleModal,
  closePopover,
  css = "p-3 pb-3",
  width = 315,
}: WorkspaceSwitcherOptionsProps) => {
  const navigate = useNavigate();
  const { tenantID } = useParams();
  const { userOwnedWorkspaces, userCollabWorkspaces, workspacesLoading } =
    useWorkspaces();
  const setWorkspaceID = useSetAtom(workspaceIdAtom);
  const mobileScreen = useAtomValue(IsMobileAtom);
  const setSidebarOpen = useSetAtom(sidebarOpenAtom);

  const openViewAllWorkspaces = () => {
    closePopover();
    navigate(`/clouds/${tenantID}/workspaces`);
    if (mobileScreen) {
      setSidebarOpen(false);
    }
  };

  const WorkspaceCard = ({ workspace }: { workspace: Workspace }) => {
    if (!workspace?.id) return null;

    return (
      <div
        key={workspace.Name}
        onClick={() => {
          setWorkspaceID(workspace.id!);
          localStorage.setItem("workspaceID", workspace.id!);
          closePopover();
          if (mobileScreen) {
            setSidebarOpen(false);
          }
        }}
      >
        <IconLink
          link={{
            id: workspace.id!,
            name: workspace.Name,
            icon: "mdi mdi-view-dashboard-outline",
            activeID: workspace.id,
            path: `/clouds/${tenantID}/workspaces/${workspace.id}/activity`,
            action: {
              icon: "mdi mdi-chevron-right",
            },
          }}
          open={true}
        />
      </div>
    );
  };

  const DisplayWorkspaces = ({
    workspaces,
    title,
  }: {
    workspaces: Workspace[];
    title: string;
  }) => {
    const filteredWorkspaces = useMemo(() => {
      return workspaces.filter((wspace) => !wspace?.Hide);
    }, [workspaces]);

    return (
      <>
        <h5 className="text-muted font-weight-500">{title}</h5>
        <div className="mx--2">
          {filteredWorkspaces.map((workspace) => (
            <WorkspaceCard key={workspace.id} workspace={workspace} />
          ))}
        </div>
      </>
    );
  };

  return (
    <div style={{ width: width, zIndex: 99 }}>
      <div className={css}>
        {workspacesLoading ? (
          <Loading />
        ) : (
          <>
            {userOwnedWorkspaces.length > 0 && (
              <div>
                <DisplayWorkspaces
                  workspaces={userOwnedWorkspaces}
                  title="My Workspaces"
                />
              </div>
            )}
            {userCollabWorkspaces?.length > 0 && (
              <div
                className={`${
                  userOwnedWorkspaces?.length > 0
                    ? "mt-3 border-top pt-3"
                    : "mt-0"
                }`}
              >
                <DisplayWorkspaces
                  workspaces={userCollabWorkspaces}
                  title="Workspaces I Collaborate On"
                />
              </div>
            )}
          </>
        )}
        {!workspacesLoading &&
          userCollabWorkspaces?.length < 1 &&
          userOwnedWorkspaces?.length < 1 && (
            <h5 className="mb-0 px-2 py-2">
              You are not a part of any workspaces.
            </h5>
          )}
      </div>
      {toggleModal && (
        <div className="p-2 border-top">
          <SelectButton
            onClick={openViewAllWorkspaces}
            disabled={false}
            icon="mdi mdi-view-grid-outline"
            title="All Workspaces"
          />
        </div>
      )}
    </div>
  );
};

export default WorkspaceSwitcherOptions;
